import React, { useState } from "react";
import { Card, Modal, Row, Typography } from "antd";
import Button from "@mui/material/Button";
import ButtonGroup from "@mui/material/ButtonGroup";
import { FullscreenRounded } from "@mui/icons-material";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Plot from "react-plotly.js";
import * as d3 from "d3-scale-chromatic";

const MAX_TICKERS_AT_ONCE = 7;

const interpolators = {
  Plasma: d3.interpolatePlasma,
    Purd: d3.interpolatePuRd,
    Turbo: d3.interpolateTurbo, // Closest to Sunset
  Warm: d3.interpolateWarm,
    Cool: d3.interpolateCool,
};

const getContrastingTextColor = (hexColor, reverse=false) => {
  // Convert hex to RGB
  const rgb = parseInt(hexColor.slice(1), 16);
  const r = (rgb >> 16) & 0xff;
  const g = (rgb >> 8) & 0xff;
  const b = rgb & 0xff;

  // Calculate luminance (sRGB formula)
  const luminance = 0.2126 * r / 255 + 0.7152 * g / 255 + 0.0722 * b / 255;

  // Return white for darker backgrounds and black for lighter backgrounds
  if (!reverse) return luminance > 0.5  ? "black" : "white";
  return luminance > 0.5 ? "white" : "black";
};



const ReusableHeatmap = ({ matrix, colorscale = "Viridis", title, reverse = false }) => {
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [visibleItems, setVisibleItems] = useState([0, MAX_TICKERS_AT_ONCE]);

  const n = Math.sqrt(matrix.length);
  const x = Array(n)
    .fill()
    .map((_, i) => matrix[i * n].x.toUpperCase());
  const y = Array(n)
    .fill()
    .map((_, i) => matrix[i].y.toUpperCase());

  const createMatrixData = (useFullMatrix = false) => {
    const startIdx = useFullMatrix ? 0 : visibleItems[0];
    const endIdx = useFullMatrix ? n : visibleItems[1];
    const matrixSize = endIdx - startIdx;

    const z = Array(matrixSize)
      .fill()
      .map(() => Array(matrixSize).fill(0));
    const text = Array(matrixSize)
      .fill()
      .map(() => Array(matrixSize).fill(""));

    for (let i = 0; i < matrixSize; i++) {
      for (let j = 0; j < matrixSize; j++) {

        const globalI = i + startIdx;
        const globalJ = j + startIdx;
        const index = globalI * n + globalJ;
        if (index >= matrix.length) continue;
        z[i][j] = matrix[index].value;
        text[i][j] = `${(matrix[index].value * 100).toFixed(1)}%`;
      }
    }

    return { z, text };
  };
  const minValue = Math.min(...matrix.map((cell) => cell.value));

  const renderHeatmap = (isFullScreen = false, fontSize = "small") => {
    const size = fontSize === "small" ? 10 : 14;
    const { z, text } = createMatrixData(isFullScreen);

    const xLabels = isFullScreen ? x : x.slice(visibleItems[0], visibleItems[1]);
    const yLabels = isFullScreen ? y : y.slice(visibleItems[0], visibleItems[1]);

    const useCustomColorscale = colorscale in interpolators;
    const customColorscale =
      useCustomColorscale &&
      Array(11)
        .fill()
        .map((_, i) => {
          const t = reverse ? 1 - i / 10 : i / 10; // Reverse the mapping if `reverse` is true
          return [i / 10, interpolators[colorscale](t)];
        });

    return (
      <Plot
        data={[
          {
            z,
            x: xLabels,
            y: yLabels,
            type: "heatmap",
            colorscale: useCustomColorscale ? customColorscale : colorscale,
          },
        ]}
        layout={{
          autosize: true,
          responsive: true,
          width: isFullScreen ? window.innerWidth * 0.9 : undefined,
          height: isFullScreen ? window.innerHeight * 0.9 : undefined,
          margin: {
            l: 60,
            r: 60,
            b: 40,
            t: 40,
          },
          colorbar: {
            tickformat: '.0%', // Multiplies by 100 and adds '%'
            tickvals: Array.from({ length: 11 }, (_, i) => i / 10), // Tick values from 0 to 1
          },
          xaxis: {
            tickfont: { size, family: "var(--font-family)", weight: 400 },
          },
          yaxis: {
            tickfont: { size, family: "var(--font-family)", weight: 400 },
          },
          annotations: text
            .map((row, i) =>
              row.map((value, j) => {
              const colorValue = useCustomColorscale
                ? interpolators[colorscale]((z[i][j] - minValue) / (1 - minValue)) // Normalize for interpolators
                : colorscale; // Default colorscale

                return {
                  x: xLabels[j],
                  y: yLabels[i],
                  text: value,
                  showarrow: false,
                  font: {
                    family: "var(--font-family)",
                    color: getContrastingTextColor(colorValue, reverse),
                    size: size - 2,
                    weight: "400",
                  },
                  bgcolor: 'transparent', // Add subtle gradient shine to the background
                };
              })
            )
            .flat(),
        }}
        config={{ displayModeBar: false }}
        style={{ width: "100%", height: "100%" }}
      />
    );
  };

  return (
    <Card className={"gapless p-2"} bordered={false}>
      <div style={{ width: "100%", maxWidth: "900px", margin: "0 auto" }}>
        <Row justify={"space-between"} align={"middle"} className={"mb-2 px-6"}>
          <Typography.Title level={4} style={{ margin: 0 }}>
            {title}
          </Typography.Title>
          <ButtonGroup
            variant="contained"
            aria-label="outlined primary button group"
            disableElevation
          >
            {n > MAX_TICKERS_AT_ONCE && (
              <Select
                defaultValue={0}
                onChange={(event) => {
                  const value = event.target.value;
                  setVisibleItems([value, value + MAX_TICKERS_AT_ONCE]);
                }}
                style={{ marginRight: 8, height: 40 }}
                variant="outlined"
                displayEmpty
              >
                {Array(Math.ceil(n / MAX_TICKERS_AT_ONCE))
                  .fill()
                  .map((_, i) => (
                    <MenuItem key={i} value={i * MAX_TICKERS_AT_ONCE}>
                      {`Items ${
                        i * MAX_TICKERS_AT_ONCE + 1
                      }-${Math.min((i + 1) * MAX_TICKERS_AT_ONCE, n)}`}
                    </MenuItem>
                  ))}
              </Select>
            )}
            <Button
              startIcon={<FullscreenRounded />}
              onClick={() => setIsModalVisible(true)}
              variant="contained"
              disableElevation={false}
            >
              Fullscreen
            </Button>
          </ButtonGroup>
        </Row>

        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          {renderHeatmap()}
        </div>
      </div>

      <Modal
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={"100vw"}
        centered
        bodyStyle={{ padding: 0 }}
      >
        <div
          style={{
            width: "100%",
            height: "calc(100vh - 60px)",
            marginLeft: "4.5vw",
            marginTop: "2.5vh",
          }}
        >
          {renderHeatmap(true, "large")}
        </div>
      </Modal>
    </Card>
  );
};

export default ReusableHeatmap;
